<template>
  <div v-if="user">
    <b-card>
      <div
        v-if="isLoading"
        class="text-center mb-2"
      >
        <b-spinner
          variant="primary"
          label="Chargement"
        />
      </div>

      <validation-observer ref="quickCreateRules">
        <b-form @submit.prevent="addOrder">
          <b-row>
            <b-col md="6">
              <b-form-group
                label="Téléphone du destinataire *"
                label-for="quick-phone"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Téléphone"
                  rules="required|tnPhone"
                >
                  <b-form-input
                    id="quick-phone"
                    v-model="order.phone1"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Téléphone"
                    maxlength="8"
                    minlength="8"
                    type="tel"
                    autocomplete="off"
                    autofocus
                    @keypress="allowOnlyNumbers"
                    @input="order.phone1 = formatPhone(order.phone1)"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Nom du destinataire *"
                label-for="quick-name"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Nom"
                  rules="required"
                >
                  <b-form-input
                    id="quick-name"
                    v-model.trim="order.name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Nom du destinataire"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Gouvernorat *"
                label-for="quick-governorate"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Gouvernorat"
                  rules="required"
                >
                  <v-select
                    id="quick-governorate"
                    v-model="order.governorate"
                    :clearable="false"
                    placeholder="Choisissez un gouvernorat"
                    label="name"
                    :options="governorates"
                    @input="onGovernorateInput"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Délégation *"
                label-for="quick-delegation"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Délégation"
                  rules="required"
                >
                  <v-select
                    id="quick-delegation"
                    v-model="order.delegation"
                    :clearable="false"
                    placeholder="Choisissez une délégation"
                    label="name"
                    :options="delegations"
                    :disabled="!order.governorate"
                    @input="onDelegationInput"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Adresse complète *"
                label-for="quick-address"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Adresse"
                  rules="required"
                >
                  <b-form-input
                    id="quick-address"
                    v-model.trim="order.address"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Adresse complète de livraison"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Montant total TTC *"
                label-for="quick-price"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Montant total TTC"
                  rules="required|priceDecimal"
                >
                  <b-form-input
                    id="quick-price"
                    v-model="order.price_ttc"
                    :formatter="formatPrice"
                    type="text"
                    inputmode="decimal"
                    placeholder="Montant total TTC"
                    :state="(errors.length > 0 || clientErrors.price_ttc || serverErrors.price_ttc) ? false : null"
                    @keypress="allowOnlyDecimalNumbers"
                    @input="validatePriceTTC"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="clientErrors.price_ttc"
                    class="text-danger d-block"
                  >{{ clientErrors.price_ttc }}</small>
                  <small
                    v-if="serverErrors.price_ttc"
                    class="text-danger d-block"
                  >{{ sanitizeError(firstError(serverErrors.price_ttc)) }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              v-if="isWeightPricing"
              md="6"
            >
              <b-form-group
                label="Poids *"
                label-for="quick-weight"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Poids"
                  rules="required"
                >
                  <v-select
                    id="quick-weight"
                    v-model="order.weight"
                    :clearable="false"
                    placeholder="Choisissez le poids"
                    label="weight"
                    :options="weightOptions"
                    :disabled="weightOptions.length === 0"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="weightOptions.length === 0"
                    class="text-danger d-block"
                  >Aucun poids configuré pour ce fournisseur.</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col :md="isWeightPricing ? 12 : 6">
              <b-form-group
                label="Produit *"
                label-for="quick-product"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Produit"
                  rules="required"
                >
                  <b-form-input
                    id="quick-product"
                    v-model.trim="product"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Désignation du produit"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <div class="quick-options">
                <b-form-checkbox
                  v-model="order.is_an_exchange"
                  switch
                  inline
                  class="quick-option"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="18"
                    class="mr-50"
                  />
                  Échange
                </b-form-checkbox>

                <b-form-checkbox
                  v-model="order.can_open_package"
                  switch
                  inline
                  class="quick-option"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="18"
                    class="mr-50"
                  />
                  Autorisation d'ouverture
                </b-form-checkbox>
              </div>
            </b-col>

            <b-col cols="12">
              <div class="quick-actions">
                <b-button
                  variant="primary"
                  type="submit"
                  class="quick-submit"
                  :disabled="isLoading || !canSubmit"
                >
                  <feather-icon
                    icon="SaveIcon"
                    class="mr-1"
                  />
                  {{ isLoading ? 'Création...' : 'Créer le colis' }}
                </b-button>

                <b-button
                  variant="outline-secondary"
                  type="reset"
                  class="quick-reset"
                  @click.prevent="clearForm"
                >
                  <b-icon
                    icon="arrow-counterclockwise"
                    class="mr-50"
                  />
                  Réinitialiser
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import storeAuth from '@/store/store'
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'

export default {
  name: 'QuickCreateOrder',
  components: {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BRow,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  data() {
    return {
      required,
      user: null,
      governorates: [],
      delegations: [],
      product: '',
      isLoading: false,
      serverErrors: {},
      clientErrors: {},
      order: this.defaultOrder(),
    }
  },
  computed: {
    isWeightPricing() {
      return this.user && (this.user.weight_price === true || this.user.pricing_mode === 'weight')
    },
    weightOptions() {
      return this.user && Array.isArray(this.user.weights) ? this.user.weights : []
    },
    canSubmit() {
      return Boolean(
        this.order.phone1
        && this.order.phone1.length === 8
        && this.order.name
        && this.order.governorate
        && this.order.delegation
        && this.order.address
        && this.order.price_ttc !== ''
        && !this.clientErrors.price_ttc
        && this.product
        && (!this.isWeightPricing || this.order.weight),
      )
    },
  },
  created() {
    this.getUser()
    this.getGovernorates()
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    defaultOrder() {
      return {
        payment_method: 0,
        name: '',
        address: '',
        phone1: '',
        price_ttc: '',
        governorate: '',
        delegation: '',
        weight: null,
        is_an_exchange: false,
        can_open_package: false,
      }
    },
    async getUser() {
      const response = await axios.get('api/v1/users/me/')
      storeAuth.commit('setUser', response.data)
      this.user = response.data
    },
    async getGovernorates() {
      const { data } = await axios.get('/api/governorates/')
      this.governorates = data
    },
    async getDelegations(governorateId) {
      if (!governorateId) {
        this.delegations = []
        return
      }
      const { data } = await axios.get('/api/delegations/', { params: { governorate: governorateId } })
      this.delegations = data
    },
    async onGovernorateInput() {
      this.order.delegation = ''
      await this.getDelegations(this.order.governorate ? this.order.governorate.id : null)
    },
    onDelegationInput(delegation) {
      if (!delegation || !delegation.governorate) return
      this.order.governorate = this.findOptionById(this.governorates, delegation.governorate.id)
        || delegation.governorate
    },
    findOptionById(options, id) {
      return (options || []).find(option => Number(option.id) === Number(id))
    },
    allowOnlyNumbers(event) {
      if (!/\d/.test(event.key)) {
        event.preventDefault()
      }
    },
    formatPhone(value) {
      return String(value || '').replace(/[^\d]/g, '').slice(0, 8)
    },
    validatePriceTTC() {
      this.$set(this.clientErrors, 'price_ttc', null)
      const val = this.order.price_ttc
      if (val === '' || val === null || typeof val === 'undefined') return
      const n = Number(val)
      if (Number.isNaN(n)) {
        this.$set(this.clientErrors, 'price_ttc', 'Le montant TTC doit être un nombre valide')
        return
      }
      if (n < 0) {
        this.$set(this.clientErrors, 'price_ttc', 'Le montant TTC ne doit pas être négatif')
        return
      }
      if (n > 1999) {
        this.$set(this.clientErrors, 'price_ttc', 'Le montant TTC ne doit pas dépasser 1999')
      }
    },
    async addOrder() {
      const isValid = await this.$refs.quickCreateRules.validate()
      if (!isValid || !this.canSubmit) {
        this.showToast('danger', 'top-center', 'champ obligatoire ! vérifier votre donnée !')
        return
      }

      this.serverErrors = {}
      this.isLoading = true

      const payload = {
        name: this.order.name,
        address: this.order.address,
        phone1: this.order.phone1,
        phone2: '',
        note: '',
        price_ttc: this.order.price_ttc,
        governorate: this.order.governorate.id,
        delegation: this.order.delegation.id,
        locality: null,
        products: [
          {
            product: this.product,
            quantity: 1,
          },
        ],
        is_an_exchange: this.order.is_an_exchange,
        exchange_article: '',
        exchange_quantity: '',
        is_fragile: false,
        is_discharge: false,
        can_open_package: this.order.can_open_package,
        payment_method: 0,
        weight: this.isWeightPricing && this.order.weight ? this.order.weight.id : null,
      }

      try {
        await axios.post('/api/orders/create/', payload)
        this.clearForm()
        this.showToast('success', 'top-center', 'Colis Ajouté avec succès')
        window.scrollTo(0, 0)
      } catch (error) {
        this.serverErrors = (error && error.response && error.response.data) ? error.response.data : {}
        const priceErr = this.serverErrors.price_ttc ? this.firstError(this.serverErrors.price_ttc) : null
        const rawMsg = priceErr || (this.serverErrors.detail ? this.firstError(this.serverErrors.detail) : 'Erreur lors de la création du colis')
        this.showToast('danger', 'top-center', this.sanitizeError(rawMsg))
      } finally {
        this.isLoading = false
      }
    },
    clearForm() {
      this.order = this.defaultOrder()
      this.product = ''
      this.delegations = []
      this.serverErrors = {}
      this.clientErrors = {}
      if (this.$refs.quickCreateRules) {
        this.$refs.quickCreateRules.reset()
      }
    },
    firstError(value) {
      if (Array.isArray(value)) return value[0]
      if (value && typeof value === 'object') {
        const first = Object.values(value)[0]
        return Array.isArray(first) ? first[0] : (first || 'Erreur')
      }
      return typeof value === 'string' ? value : 'Erreur'
    },
    sanitizeError(text) {
      if (!text) return ''
      let t = String(text)
      t = t.replace(/\bprice_ttc\b:?/gi, '')
      t = t.replace(/[_]+/g, ' ')
      t = t.replace(/^[\s:;-]+/, '').replace(/[\s:;-]+$/, '')
      t = t.replace(/[()]/g, '')
      return t.trim()
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.quick-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  padding: 0.5rem 0 1rem;
}

.quick-option {
  margin-right: 0;
  font-weight: 500;
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.quick-submit,
.quick-reset {
  min-height: 42px;
}

@media (max-width: 575.98px) {
  .quick-actions {
    flex-direction: column;
  }

  .quick-submit,
  .quick-reset {
    width: 100%;
  }
}
</style>
